import { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Activity, Bot, CalendarDays, Home, LineChart, LogOut, MoreHorizontal, Settings2, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup } from "@/components/BrandMark";
import { MODULES, TONES } from "@/components/common";
import { NotificationBell } from "@/components/NotificationBell";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

/** Máximo de pestañas en la barra inferior (incluida «Más» si hace falta). */
const MAX_BOTTOM_SLOTS = 5;

const useNav = () => {
  const { me } = useAuth();
  const mods = Object.entries(MODULES)
    .filter(([k]) => me.modules?.[k])
    .map(([k, m]) => ({ key: k, to: m.path, label: m.label, icon: m.icon, tone: m.tone }));
  return [
    { key: "home", to: "/", label: "Inicio", icon: Home, tone: "ink" },
    ...mods,
    { key: "agenda", to: "/agenda", label: "Agenda", icon: CalendarDays, tone: "ink" },
    { key: "coach", to: "/coach", label: "Coach", icon: Bot, tone: "ink" },
    { key: "progress", to: "/progreso", label: "Progreso", icon: LineChart, tone: "streak" },
    { key: "profile", to: "/perfil", label: "Perfil", icon: UserRound, tone: "ink" },
  ];
};

const useExtraNav = () => {
  const { me } = useAuth();
  return [
    { key: "activity", to: "/actividad", label: "Importar actividad", icon: Activity, tone: "train" },
    ...(me.role === "admin" ? [{ key: "admin", to: "/admin", label: "Conectores", icon: Settings2, tone: "ink" }] : []),
  ];
};

const Sidebar = ({ items, extra }) => {
  const { me, logout } = useAuth();
  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-black/5 bg-white px-5 py-8 lg:flex">
      <div className="px-2">
        <BrandLockup useImage />
      </div>
      <nav className="mt-10 flex flex-1 flex-col gap-1">
        {[...items, ...extra].map((it) => (
          <NavLink key={it.key} to={it.to} end={it.to === "/"} data-testid={`sidebar-nav-${it.key}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? `${TONES[it.tone].soft} ${TONES[it.tone].text}` : "text-stone-600 hover:bg-stone-50"}`}>
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="rounded-2xl bg-stone-50 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-ink text-sm font-bold text-white" data-testid="sidebar-user-avatar">
            {me.avatar_url ? <img src={me.avatar_url} alt="" className="h-full w-full object-cover" /> : (me.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{me.name}</p>
            <p className="truncate text-xs text-stone-500">{me.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="sidebar-logout-btn" className="mt-3 flex items-center gap-2 text-xs font-semibold text-stone-600 hover:text-ink">
          <LogOut className="h-3.5 w-3.5" /> Cerrar sesión
        </button>
      </div>
    </aside>
  );
};

const NavTab = ({ it, active }) => (
  <NavLink
    to={it.to}
    end={it.to === "/"}
    data-testid={`bottom-nav-${it.key}`}
    className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 text-[10px] font-semibold transition-colors ${active ? TONES[it.tone].text : "text-stone-400"}`}
  >
    <span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors ${active ? TONES[it.tone].soft : ""}`}>
      <it.icon className="h-5 w-5" />
    </span>
    <span className="truncate">{it.label}</span>
  </NavLink>
);

const BottomNav = ({ items, extra }) => {
  const { logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const { primary, overflow } = useMemo(() => {
    const needsMore = items.length + extra.length > MAX_BOTTOM_SLOTS;
    if (!needsMore) return { primary: items, overflow: [...extra] };
    const slots = MAX_BOTTOM_SLOTS - 1;
    return {
      primary: items.slice(0, slots),
      overflow: [...items.slice(slots), ...extra],
    };
  }, [items, extra]);

  const pathActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));
  const moreActive = overflow.some((it) => pathActive(it.to));
  const showMore = overflow.length > 0;

  const openFromMore = (to) => {
    setMoreOpen(false);
    nav(to);
  };

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-black/5 bg-white/80 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden" data-testid="bottom-nav">
        <div className="mx-auto flex max-w-lg justify-around px-1 py-1.5">
          {primary.map((it) => (
            <NavTab key={it.key} it={it} active={pathActive(it.to)} />
          ))}
          {showMore && (
            <button
              type="button"
              data-testid="bottom-nav-more"
              onClick={() => setMoreOpen(true)}
              className={`flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 text-[10px] font-semibold transition-colors ${moreActive || moreOpen ? "text-ink" : "text-stone-400"}`}
            >
              <span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors ${moreActive || moreOpen ? "bg-stone-100" : ""}`}>
                <MoreHorizontal className="h-5 w-5" />
              </span>
              <span className="truncate">Más</span>
            </button>
          )}
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] rounded-t-3xl border-0 bg-white px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 lg:hidden"
          data-testid="more-sheet"
        >
          <SheetHeader className="mb-4 text-left">
            <SheetTitle className="font-display text-2xl font-extrabold text-ink">Más</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-x-2 gap-y-5" data-testid="more-sheet-grid">
            {overflow.map((it) => {
              const active = pathActive(it.to);
              return (
                <button
                  key={it.key}
                  type="button"
                  data-testid={`more-nav-${it.key}`}
                  onClick={() => openFromMore(it.to)}
                  className="flex flex-col items-center gap-2 rounded-2xl px-2 py-2 transition-colors active:bg-stone-50"
                >
                  <span className={`flex h-12 w-12 items-center justify-center rounded-2xl ${active ? TONES[it.tone].soft : "bg-stone-100"}`}>
                    <it.icon className={`h-6 w-6 ${active ? TONES[it.tone].text : "text-ink"}`} />
                  </span>
                  <span className={`text-center text-xs font-semibold leading-tight ${active ? TONES[it.tone].text : "text-stone-700"}`}>
                    {it.label}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => { setMoreOpen(false); logout(); }}
            data-testid="more-logout-btn"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-red-200 bg-red-50 py-3.5 text-sm font-semibold text-red-600 transition-transform active:scale-[0.98]"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export const AppShell = () => {
  const items = useNav();
  const extra = useExtraNav();
  return (
    <div className="min-h-screen">
      <Sidebar items={items} extra={extra} />
      <div className="fixed right-4 top-4 z-50 lg:right-8 lg:top-6">
        <NotificationBell />
      </div>
      <main className="px-4 pb-28 pt-6 sm:px-6 lg:ml-64 lg:px-10 lg:pb-12 lg:pt-10">
        <div className="mx-auto max-w-5xl">
          <Outlet />
        </div>
      </main>
      <BottomNav items={items} extra={extra} />
    </div>
  );
};
